import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/generated';
import type { FieldValues, ManagerCreateFlags, SportType as ApiSportType } from '@/api/generated/model';
import { useAuthStore, useUIStore } from '@/store';
import { parseJwt } from '@/utils/jwt';
import { queryKeys } from './useApi';

export const manageKeys = {
  manager: ['manager'] as const,
  mePlayer: ['me', 'player'] as const,
  presets: (sportType?: string) => ['tournament-presets', sportType] as const,
  settingsForm: (id: string) => ['tournaments', id, 'settings-form'] as const,
  workflow: (id: string) => ['tournaments', id, 'workflow'] as const,
  liveConfig: (id: string) => ['matches', id, 'live-config'] as const,
};

export const useManager = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: manageKeys.manager,
    queryFn: () => {
      const token = localStorage.getItem('auth_token') || useAuthStore.getState().token;
      if (!token) return Promise.reject(new Error('no token'));
      return api.getApiV1Manager();
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message === 'no token') return false;
      return failureCount < 2;
    },
  });
};

const noCreate: ManagerCreateFlags = {};

/**
 * Режим «Админ». Кнопка держится на последнем подтверждённом `canManage`:
 * загрузка и повторный запрос её не прячут. Сброс — только logout или ответ сервера,
 * что права уже нет.
 */
export const useAdminMode = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const canManage = useAuthStore((s) => s.canManage);
  const setCanManage = useAuthStore((s) => s.setCanManage);
  const storedMode = useUIStore((s) => s.adminMode);
  const setAdminMode = useUIStore((s) => s.setAdminMode);
  const { data: manager, isFetchedAfterMount, isFetching } = useManager();
  const canManageNow = isAuthenticated && canManage;
  const managerSettled = isFetchedAfterMount && !isFetching && Boolean(manager);

  useEffect(() => {
    if (!isAuthenticated || !managerSettled || !manager) return;
    setCanManage(Boolean(manager.canManageAnything));
  }, [isAuthenticated, managerSettled, manager, setCanManage]);

  useEffect(() => {
    if (!storedMode) return;
    if (!isAuthenticated || (managerSettled && manager && !manager.canManageAnything)) {
      setAdminMode(false);
    }
  }, [storedMode, isAuthenticated, managerSettled, manager, setAdminMode]);

  const adminMode = storedMode && canManageNow;
  const toggle = useCallback(() => setAdminMode(!storedMode), [setAdminMode, storedMode]);

  return {
    adminMode,
    canManage: canManageNow,
    toggle,
    manager,
    isSuperAdmin: Boolean(manager?.isSuperAdmin),
    create: adminMode ? manager?.create ?? noCreate : noCreate,
    /** Право с сервера с учётом режима. */
    can: (permission?: boolean) => adminMode && Boolean(permission),
  };
};

export const useMePlayer = () => {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: manageKeys.mePlayer,
    queryFn: () => api.getApiV1MePlayer(),
    enabled: isAuthenticated,
  });
};

/** Подпись текущего пользователя: имя связанного игрока, иначе email из токена. */
export const useCurrentUserLabel = (): string => {
  const token = useAuthStore((s) => s.token);
  const { data: me } = useMePlayer();
  const name = [me?.player?.firstName, me?.player?.lastName].filter(Boolean).join(' ');
  return name || parseJwt(token)?.sub || '';
};

export const useTournamentPresets = (sportType?: string) =>
  useQuery({
    queryKey: manageKeys.presets(sportType),
    queryFn: () => api.getApiV1TournamentPresets(sportType ? { sportType: sportType as ApiSportType } : undefined),
    enabled: Boolean(sportType),
  });

export const useCreateFromPreset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ presetId, values }: { presetId: string; values: FieldValues }) =>
      api.postApiV1TournamentPresetsPresetIdCreate(presetId, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tournaments.all });
      qc.invalidateQueries({ queryKey: manageKeys.manager });
    },
  });
};

export const useSettingsForm = (tournamentId: string) =>
  useQuery({
    queryKey: manageKeys.settingsForm(tournamentId),
    queryFn: () => api.getApiV1TournamentsTournamentIdSettingsForm(tournamentId),
    enabled: Boolean(tournamentId),
  });

export const useUpdateSettingsForm = (tournamentId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: FieldValues) => api.patchApiV1TournamentsTournamentIdSettingsForm(tournamentId, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tournaments.detail(tournamentId) });
      qc.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

export const useTournamentWorkflow = (tournamentId: string, enabled = true) =>
  useQuery({
    queryKey: manageKeys.workflow(tournamentId),
    queryFn: () => api.getApiV1TournamentsTournamentIdWorkflow(tournamentId),
    enabled: Boolean(tournamentId) && enabled,
  });

export const useRunTournamentAction = (tournamentId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId, values }: { actionId: string; values?: FieldValues }) =>
      api.postApiV1TournamentsTournamentIdActionsActionId(tournamentId, actionId, values ?? {}),
    onSuccess: (result) => {
      if (result.workflow) {
        qc.setQueryData(manageKeys.workflow(tournamentId), result.workflow);
      }
      qc.invalidateQueries({ queryKey: queryKeys.tournaments.detail(tournamentId) });
      qc.invalidateQueries({ queryKey: ['tournaments', tournamentId, 'registrations'] });
      qc.invalidateQueries({ queryKey: queryKeys.tournaments.list() });
      qc.invalidateQueries({ queryKey: queryKeys.matches.all });
      qc.invalidateQueries({ queryKey: queryKeys.home.all });
      qc.invalidateQueries({ queryKey: manageKeys.manager });
    },
  });
};

export const useMatchLiveConfig = (matchId: string) =>
  useQuery({
    queryKey: manageKeys.liveConfig(matchId),
    queryFn: () => api.getApiV1MatchesMatchIdLiveConfig(matchId),
    enabled: Boolean(matchId),
  });

export const useRunMatchAction = (matchId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId, values }: { actionId: string; values?: FieldValues }) =>
      api.postApiV1MatchesMatchIdActionsActionId(matchId, actionId, values ?? {}),
    onSuccess: (match) => {
      qc.invalidateQueries({ queryKey: queryKeys.matches.detail(matchId) });
      qc.invalidateQueries({ queryKey: manageKeys.liveConfig(matchId) });
      if (match.tournamentId) {
        qc.invalidateQueries({ queryKey: ['tournaments', match.tournamentId] });
      }
      qc.invalidateQueries({ queryKey: manageKeys.manager });
    },
  });
};
